import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    TextField,
} from '@mui/material';
import { Login as RaLogin, useLogin, useNotify } from 'react-admin';
import BrandLogo from '@/assets/logo/BrandLogo';
import { warmCryptoHandshake } from '@/services/cryptoSession';

export default function Login() {
    const login = useLogin();
    const notify = useNotify();
    const lastSubmitAtRef = useRef(0);

    useEffect(() => {
        warmCryptoHandshake();
    }, []);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        const now = Date.now();
        if (now - lastSubmitAtRef.current < 800) return;
        lastSubmitAtRef.current = now;

        if (!username.trim() || !password) {
            notify('Please input username and password', { type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            await login({ username: username.trim(), password } as any);
        } catch (err: any) {
            if (err?.code === 303) {
                return;
            }
            notify(err?.message || 'Login failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <RaLogin
            avatarIcon={null}
            sx={{
                background: 'none',
                justifyContent: 'center',
                alignItems: 'center',
                '& .RaLogin-card': {
                    marginTop: 0,
                    marginBottom: 0,
                    transform: 'translateY(-180px)',
                    boxShadow: 'none',
                    border: 'none',
                    backgroundColor: 'transparent',
                    position: 'relative',
                    overflow: 'hidden',
                },
                '& .RaLogin-avatar': {
                    display: 'none',
                },
            }}
        >
            <Box
                component="form"
                onSubmit={onSubmit}
                sx={{ width: 380, px: 2, pb: 2, position: 'relative' }}
            >
                {loading ? (
                    <Box
                        aria-busy
                        aria-live="polite"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(11, 31, 58, 0.55)'
                                    : 'rgba(255, 255, 255, 0.65)',
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={36} />
                    </Box>
                ) : null}
                <Stack spacing={2}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            pt: 1.5,
                            pb: 0.5,
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    >
                        <BrandLogo width={220} />
                    </Box>
                    <TextField
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        autoComplete="username"
                        disabled={loading}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        autoComplete="current-password"
                        disabled={loading}
                    />
                    <Button type="submit" variant="contained" disabled={loading} fullWidth>
                        {loading ? 'Signing in...' : 'Sign in'}
                    </Button>
                </Stack>
            </Box>
        </RaLogin>
    );
}
